import { useMemo } from 'react'
import { DjayAccessory, DjayTier } from './DjayAvatar'
import {
  ACHIEVEMENTS,
  AchievementCheckData,
} from '../components/progress/AchievementsSection'
import { MODULES } from '../content'
import { VariantProgress } from './useVariantUnlocks'

export type AccessoryCategory = 'hoofd' | 'gezicht' | 'hals' | 'hand' | 'effect'

export interface AccessoryUnlockInfo {
  id: DjayAccessory
  name: string
  unlocked: boolean
  condition: string
  progress: VariantProgress
  category: AccessoryCategory | 'none'
}

export interface TierUnlockInfo {
  id: DjayTier
  name: string
  unlocked: boolean
  condition: string
  progress: VariantProgress
}

// Map accessory IDs to human-readable names
export const ACCESSORY_NAMES: Record<DjayAccessory, string> = {
  none: 'Geen',
  seedling: 'Sprietje',
  pencil: 'Potlood',
  badge100: '100 Badge',
  smallFlame: 'Vlammetje',
  trophy: 'Trofee',
  doubleFlameCrown: 'Vuurkroon',
  starWand: 'Sterrenstaf',
  tripleStars: 'Drie Sterren',
  scepter: 'Scepter',
  leafGarland: 'Lauwerkrans',
  medal: 'Medaille',
  cosmicOrb: 'Kosmische Bol',
  shadowOrb: 'Schaduw Bol',
  sunglasses: 'Zonnebril',
  cap: 'Petje',
  goldChain: 'Gouden Ketting',
}

export const ACCESSORY_CATEGORIES: Record<
  DjayAccessory,
  AccessoryCategory | 'none'
> = {
  none: 'none',
  seedling: 'hand',
  pencil: 'hand',
  badge100: 'hals',
  smallFlame: 'hand',
  trophy: 'hand',
  doubleFlameCrown: 'hoofd',
  starWand: 'hand',
  tripleStars: 'effect',
  scepter: 'hand',
  leafGarland: 'hals',
  medal: 'hals',
  cosmicOrb: 'hand',
  shadowOrb: 'hand',
  sunglasses: 'gezicht',
  cap: 'hoofd',
  goldChain: 'hals',
}

export const CATEGORY_LABELS: Record<AccessoryCategory, string> = {
  hoofd: 'Hoofd',
  gezicht: 'Gezicht',
  hals: 'Hals',
  hand: 'In de hand',
  effect: 'Effect',
}

// Map tier IDs to human-readable names
const TIER_NAMES: Record<DjayTier, string> = {
  rookie: 'Beginner',
  pro: 'Pro',
  master: 'Meester',
}

export function useDjayCustomization(
  checkData: AchievementCheckData,
  unlockedCosmetics: string[] = [],
) {
  return useMemo(() => {
    // 1. Accessories
    const accessoryMap = new Map<DjayAccessory, AccessoryUnlockInfo>()

    // Always add 'none'
    accessoryMap.set('none', {
      id: 'none',
      name: ACCESSORY_NAMES.none,
      unlocked: true,
      condition: 'Altijd beschikbaar',
      progress: { current: 1, max: 1, label: 'Ontgrendeld' },
      category: 'none',
    })

    // Map from achievements
    ACHIEVEMENTS.forEach((achievement) => {
      if (achievement.accessory && achievement.accessory !== 'none') {
        const isUnlocked = achievement.check(checkData)
        const progress = achievement.getProgress
          ? achievement.getProgress(checkData)
          : { current: isUnlocked ? 1 : 0, max: 1, label: 'Ontgrendeld' }

        // If multiple achievements grant the same accessory, keep the one that is unlocked, or the first one
        const existing = accessoryMap.get(achievement.accessory)
        if (!existing || (!existing.unlocked && isUnlocked)) {
          accessoryMap.set(achievement.accessory, {
            id: achievement.accessory,
            name:
              ACCESSORY_NAMES[achievement.accessory] || achievement.accessory,
            unlocked: isUnlocked,
            condition: achievement.description,
            progress,
            category: ACCESSORY_CATEGORIES[achievement.accessory] || 'hand',
          })
        }
      }
    })

    // Standalone wearables with their own simple, earnable gates.
    // These reuse existing checkData signals (NOT variant thresholds or
    // mastery logic) so each shows a real "Ontgrendel door…" path + progress
    // bar on the front UI.
    const standaloneAccessories: {
      id: DjayAccessory
      condition: string
      current: number
      max: number
      label: string
    }[] = [
      {
        id: 'cap',
        condition: 'Beantwoord 25 vragen',
        current: Math.min(checkData.totalQuestionsAnswered, 25),
        max: 25,
        label: 'vragen',
      },
      {
        id: 'sunglasses',
        condition: 'Bereik level 3',
        current: Math.min(checkData.level, 3),
        max: 3,
        label: 'level',
      },
      {
        id: 'goldChain',
        condition: 'Haal 5 keer je dagdoel',
        current: Math.min(checkData.totalDailyGoalsReached, 5),
        max: 5,
        label: 'dagdoelen',
      },
    ]
    standaloneAccessories.forEach((acc) => {
      if (!accessoryMap.has(acc.id)) {
        // Unlocked if the live stat gate is met OR it was previously earned and
        // persisted (so a cosmetic stays "owned" even if a live stat dips, e.g.
        // currentWeekDays resetting on a new week).
        const gateMet = acc.current >= acc.max
        const persisted = unlockedCosmetics.includes(acc.id)
        const unlocked = gateMet || persisted
        accessoryMap.set(acc.id, {
          id: acc.id,
          name: ACCESSORY_NAMES[acc.id],
          unlocked,
          condition: acc.condition,
          progress: {
            current: unlocked ? acc.max : acc.current,
            max: acc.max,
            label: unlocked ? 'Ontgrendeld' : acc.label,
          },
          category: ACCESSORY_CATEGORIES[acc.id],
        })
      }
    })

    const accessories = Array.from(accessoryMap.values())

    // 2. Tiers
    const tiers: TierUnlockInfo[] = [
      {
        id: 'rookie',
        name: TIER_NAMES.rookie,
        unlocked: true,
        condition: 'Altijd beschikbaar',
        progress: { current: 1, max: 1, label: 'Ontgrendeld' },
      },
      {
        id: 'pro',
        name: TIER_NAMES.pro,
        unlocked: checkData.masteredCount >= 1 && checkData.level >= 5,
        condition: 'Level 5 bereikt & 1 vak afgerond',
        progress: {
          current:
            (checkData.level >= 5 ? 1 : 0) +
            (checkData.masteredCount >= 1 ? 1 : 0),
          max: 2,
          label: 'doelen',
        },
      },
      {
        id: 'master',
        name: TIER_NAMES.master,
        unlocked:
          checkData.masteredCount >= MODULES.length && checkData.level >= 15,
        condition: `Level 15 bereikt & alle ${MODULES.length} vakken afgerond`,
        progress: {
          current:
            (checkData.level >= 15 ? 1 : 0) +
            (checkData.masteredCount >= MODULES.length ? 1 : 0),
          max: 2,
          label: 'doelen',
        },
      },
    ]

    return {
      accessories,
      tiers,
      unlockedAccessoriesCount: accessories.filter((a) => a.unlocked).length,
      unlockedTiersCount: tiers.filter((t) => t.unlocked).length,
    }
  }, [checkData, unlockedCosmetics])
}

/**
 * The standalone (non-achievement) cosmetic ids and their live stat gates.
 * Exported so the collection page can persist a cosmetic the moment its gate is
 * first met (via markCosmeticUnlocked) without duplicating the gate logic.
 */
export const STANDALONE_COSMETIC_GATES: {
  id: DjayAccessory
  isMet: (checkData: AchievementCheckData) => boolean
}[] = [
  { id: 'cap', isMet: (d) => d.totalQuestionsAnswered >= 25 },
  { id: 'sunglasses', isMet: (d) => d.level >= 3 },
  { id: 'goldChain', isMet: (d) => d.totalDailyGoalsReached >= 5 },
]
